import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Download, 
  Eye, 
  Trash2, 
  Plus,
  Target,
  BookOpen,
  Users,
  TrendingUp,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

interface PlacementQuestion {
  id: number;
  question_number: number;
  level: 'Pre-A1' | 'A1' | 'A2';
  question_text: string;
  options: string[];
  correct_answer: string;
  points: number;
}

interface PlacementStats {
  total_questions: number;
  'Pre-A1_questions': number;
  'A1_questions': number;
  'A2_questions': number;
}

const PlacementAdmin: React.FC = () => {
  const [questions, setQuestions] = useState<PlacementQuestion[]>([]);
  const [stats, setStats] = useState<PlacementStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionsResponse, statsResponse] = await Promise.all([
        axios.get('/placement/questions'),
        axios.get('/placement/stats')
      ]);

      setQuestions(questionsResponse.data.questions);
      setStats(statsResponse.data.stats);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      setUploadResult(null);

      const formData = new FormData();
      formData.append('excelFile', file);

      const response = await axios.post('/placement/upload-questions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      
      if (response.data.success) {
        // Recargar datos
        await fetchData();
      }

    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.error || 'Error subiendo archivo');
    } finally {
      setUploading(false);
    }
  };

  const exportReport = async () => {
    try {
      const response = await axios.get('/placement/export-report', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reporte_ubicacion.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Error exportando reporte');
    }
  };

  const getLevelInfo = (level: string) => {
    switch (level) {
      case 'Pre-A1':
        return { name: 'Pre-A1', color: '#ff6b6b', emoji: '🚀' };
      case 'A1':
        return { name: 'A1', color: '#4ecdc4', emoji: '🎯' };
      case 'A2':
        return { name: 'A2', color: '#45b7d1', emoji: '🌟' };
      default:
        return { name: level, color: '#666', emoji: '❓' };
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Cargando administración de ubicación... 🎯
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="title"
      >
        <h1>
          <span className="emoji">🎯</span>
          Administración de Ubicación
          <span className="emoji">👑</span>
        </h1>
        <h2>Gestiona Preguntas y Reportes de Ubicación</h2>
      </motion.div>

      {/* Estadísticas */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-4"
        >
          <div className="card" style={{ textAlign: 'center' }}>
            <BookOpen style={{ fontSize: '2rem', color: '#4ecdc4', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.total_questions}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Total Preguntas</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', color: '#ff6b6b', marginBottom: '10px' }}>🚀</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats['Pre-A1_questions']}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Pre-A1</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', color: '#4ecdc4', marginBottom: '10px' }}>🎯</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats['A1_questions']}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>A1</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', color: '#45b7d1', marginBottom: '10px' }}>🌟</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats['A2_questions']}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>A2</p>
          </div>
        </motion.div>
      )}

      {/* Herramientas de administración */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <h3 style={{ color: '#333', marginBottom: '20px', textAlign: 'center' }}>
          🛠️ Herramientas de Administración
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {/* Cargar preguntas desde Excel */}
          <div style={{ textAlign: 'center' }}>
            <Upload style={{ fontSize: '3rem', color: '#4ecdc4', marginBottom: '15px' }} />
            <h4 style={{ color: '#333', marginBottom: '10px' }}>Cargar Preguntas</h4>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
              Sube un archivo Excel con las preguntas de ubicación
            </p>
            
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
              />
              <button 
                className="btn"
                disabled={uploading}
                style={{ width: '100%' }}
              >
                {uploading ? (
                  <>
                    <div className="spinner" style={{ 
                      width: '20px', 
                      height: '20px', 
                      marginRight: '8px',
                      display: 'inline-block'
                    }}></div>
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload style={{ marginRight: '8px' }} />
                    Seleccionar Excel
                  </>
                )}
              </button>
            </div>

            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              <FileText style={{ display: 'inline', marginRight: '5px' }} />
              Formato: .xlsx, .xls
            </div>
          </div>

          {/* Exportar reporte */}
          <div style={{ textAlign: 'center' }}>
            <Download style={{ fontSize: '3rem', color: '#ff6b6b', marginBottom: '15px' }} />
            <h4 style={{ color: '#333', marginBottom: '10px' }}>Exportar Reporte</h4>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
              Descarga reporte de usuarios con sus niveles asignados
            </p>
            
            <button 
              onClick={exportReport}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              <Download style={{ marginRight: '8px' }} />
              Descargar Excel
            </button>

            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              <Users style={{ display: 'inline', marginRight: '5px' }} />
              Incluye todos los usuarios
            </div>
          </div>

          {/* Ver reporte en pantalla */}
          <div style={{ textAlign: 'center' }}>
            <Eye style={{ fontSize: '3rem', color: '#45b7d1', marginBottom: '15px' }} />
            <h4 style={{ color: '#333', marginBottom: '10px' }}>Ver Reporte</h4>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
              Visualiza resultados de ubicación en pantalla
            </p>
            
            <button 
              onClick={() => window.open('/api/placement/report', '_blank')}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              <Eye style={{ marginRight: '8px' }} />
              Ver Reporte
            </button>

            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              <TrendingUp style={{ display: 'inline', marginRight: '5px' }} />
              Estadísticas en tiempo real
            </div>
          </div>
        </div>
      </motion.div>

      {/* Resultado de carga */}
      {uploadResult && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`alert ${uploadResult.success ? 'alert-success' : 'alert-error'}`}
          style={{ marginTop: '20px' }}
        >
          {uploadResult.success ? (
            <>
              <CheckCircle style={{ display: 'inline', marginRight: '8px' }} />
              {uploadResult.message}
              {uploadResult.importedCount && (
                <div style={{ marginTop: '10px', fontSize: '14px' }}>
                  <strong>Preguntas importadas:</strong> {uploadResult.importedCount}
                </div>
              )}
            </>
          ) : (
            <>
              <AlertCircle style={{ display: 'inline', marginRight: '8px' }} />
              Error en la carga
            </>
          )}
          
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              <strong>Errores encontrados:</strong>
              <ul style={{ margin: '5px 0 0 20px' }}>
                {uploadResult.errors.slice(0, 5).map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
                {uploadResult.errors.length > 5 && (
                  <li>... y {uploadResult.errors.length - 5} errores más</li>
                )}
              </ul>
            </div>
          )}
        </motion.div>
      )}

      {/* Lista de preguntas */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3 style={{ color: 'white', marginBottom: '20px' }}>
          📚 Preguntas de Ubicación ({questions.length})
        </h3>

        {questions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center' }}>
            <h3>📝 No hay preguntas cargadas</h3>
            <p>Sube un archivo Excel con las preguntas para comenzar</p>
          </div>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            {questions.map((question, index) => {
              const levelInfo = getLevelInfo(question.level);
              return (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="card"
                  style={{ marginBottom: '15px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ 
                          background: levelInfo.color, 
                          color: 'white', 
                          padding: '5px 10px', 
                          borderRadius: '15px', 
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '10px'
                        }}>
                          {levelInfo.emoji} {levelInfo.name}
                        </span>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Pregunta #{question.question_number}
                        </span>
                      </div>
                      <h4 style={{ color: '#333', margin: '0 0 10px 0', fontSize: '16px' }}>
                        {question.question_text}
                      </h4>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                    {question.options.map((option, optIndex) => (
                      <div
                        key={optIndex}
                        style={{
                          background: option === question.correct_answer ? '#56ab2f20' : 'rgba(0,0,0,0.05)',
                          border: `2px solid ${option === question.correct_answer ? '#56ab2f' : 'transparent'}`,
                          padding: '10px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      >
                        <strong>{String.fromCharCode(65 + optIndex)}.</strong> {option}
                        {option === question.correct_answer && (
                          <CheckCircle style={{ 
                            display: 'inline', 
                            marginLeft: '8px', 
                            color: '#56ab2f',
                            fontSize: '16px'
                          }} />
                        )}
                      </div>
                    ))}
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    <span>Puntos: {question.points}</span>
                    <span>Respuesta correcta: {question.correct_answer}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: '20px' }}>
          😔 {error}
        </div>
      )}
    </div>
  );
};

export default PlacementAdmin;
